import { useState } from 'react';
import { Box, Button, Flex, HStack, IconButton, Input, Select, Text, Tooltip } from '@chakra-ui/react';
import { RiDeleteBinLine } from 'react-icons/ri';

import useAppToast from 'components/toast';
import { useCampaignUI } from '../_ui/ui';
import { useListSegmentsQuery, useSaveSegmentMutation, useDeleteSegmentMutation } from 'api/metaWhatsappApi';
import campaignError from '../campaignErrors';

/**
 * Saved audience filters (M5): load one, or keep the current one for next time.
 *
 * ★ LOADING REPLACES THE FILTER, IT DOES NOT MERGE INTO IT. Merging would leave
 * whatever the person had already typed sitting underneath the segment they
 * chose, so "Dubai investors" would quietly mean "Dubai investors who are also
 * whatever I was looking at a minute ago" — and the only clue would be a
 * recipient count that looked slightly wrong.
 *
 * ★ A SEGMENT IS SHARED, AND SAFELY SO. It stores the question, never the
 * answer: the audience is resolved with the campaign creator's own lead scope
 * at plan time, so opening a colleague's segment shows you only leads you could
 * already see. That is why there is no ownership to display here.
 */
export default function SegmentBar({ filter, onLoad }) {
	const ui = useCampaignUI();
	const notify = useAppToast();

	const { data: segments = [] } = useListSegmentsQuery();
	const [save, { isLoading: isSaving }] = useSaveSegmentMutation();
	const [remove, { isLoading: isRemoving }] = useDeleteSegmentMutation();

	const [selected, setSelected] = useState('');
	const [name, setName] = useState('');

	// An empty filter means "everyone this person can see" — worth building on
	// purpose, never worth saving under a name that hides it.
	const hasFilter = Object.values(filter ?? {}).some((v) => v !== undefined && v !== null && v !== '');

	const load = (id) => {
		setSelected(id);
		if (!id) return;
		const segment = segments.find((s) => s._id === id);
		if (!segment) return;
		onLoad(segment.filter ?? {});
		setName(segment.name);
		notify.success(`Loaded “${segment.name}”`, 'The filters below now match that segment.');
	};

	const onSave = async () => {
		try {
			await save({ name: name.trim(), filter }).unwrap();
			notify.success(`Saved “${name.trim()}”`, 'Anyone building a campaign can use it now.');
		} catch (err) {
			notify.error('Could not save the segment', campaignError(err));
		}
	};

	const onDelete = async () => {
		const segment = segments.find((s) => s._id === selected);
		try {
			await remove(selected).unwrap();
			setSelected('');
			// Worth saying: the obvious fear when deleting a shared thing is that
			// it breaks something already running.
			notify.success(
				`Removed “${segment?.name ?? 'segment'}”`,
				'Campaigns already built from it keep their audience.',
			);
		} catch (err) {
			notify.error('Could not remove the segment', campaignError(err));
		}
	};

	return (
		<Box mb={4} p={3} borderWidth='1px' borderColor={ui.borderSubtle} borderRadius='lg' bg={ui.surfaceSubtle}>
			<Text fontSize='xs' color={ui.textMuted} mb={2}>
				Saved segments
			</Text>

			<Flex gap={3} wrap='wrap' align='center'>
				<HStack spacing={2} flex='1 1 260px' minW='240px'>
					<Select
						size='sm'
						value={selected}
						onChange={(e) => load(e.target.value)}
						bg={ui.inputBg}
						borderColor={ui.border}
						color={ui.textPrimary}
						placeholder={segments.length ? 'Load a saved segment…' : 'Nothing saved yet'}
						isDisabled={!segments.length}
					>
						{segments.map((s) => (
							<option key={s._id} value={s._id}>
								{s.name}
								{Number.isFinite(s.approxSize) ? ` · about ${s.approxSize.toLocaleString()}` : ''}
							</option>
						))}
					</Select>
					{selected && (
						<Tooltip hasArrow label='Remove this saved segment for everyone'>
							<IconButton
								size='sm'
								variant='ghost'
								color={ui.danger}
								aria-label='Remove segment'
								icon={<RiDeleteBinLine />}
								isLoading={isRemoving}
								onClick={onDelete}
							/>
						</Tooltip>
					)}
				</HStack>

				<HStack spacing={2} flex='1 1 260px' minW='240px'>
					<Input
						size='sm'
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder='Name these filters to reuse them'
						maxLength={80}
						bg={ui.inputBg}
						borderColor={ui.border}
						color={ui.textPrimary}
					/>
					<Tooltip
						hasArrow
						label={hasFilter ? 'Saving over an existing name updates it' : 'Choose at least one filter first'}
					>
						{/* A span so the tooltip still shows while the button is disabled. */}
						<Box as='span'>
							<Button
								size='sm'
								variant='neutralOutline'
								borderColor={ui.border}
								color={ui.textPrimary}
								isLoading={isSaving}
								isDisabled={!name.trim() || !hasFilter}
								onClick={onSave}
							>
								Save
							</Button>
						</Box>
					</Tooltip>
				</HStack>
			</Flex>
		</Box>
	);
}
